// useFacebookPixel.js
import { useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { useApolloClient } from "@apollo/client";
import { SEND_META_CONVERSION_EVENT } from "../api/meta/mutations";

/**
 * Génère un event_id unique pour le dédoublonnage Meta Pixel / CAPI.
 * @param {string} prefix - Préfixe optionnel (ex: "purchase")
 * @returns {string} - Identifiant unique
 */
const generateEventId = (prefix = "event") => `${prefix}_${uuidv4()}`;

/**
 * Lit une valeur de cookie par nom.
 */
const getCookie = (name) => {
  if (typeof document === "undefined" || !document.cookie) return null;
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
};

/**
 * Récupère le paramètre fbclid dans l’URL (présent au clic sur une pub Meta).
 */
const getFbclidFromUrl = () => {
  if (typeof window === "undefined" || !window.location?.search) return null;
  const params = new URLSearchParams(window.location.search);
  return params.get("fbclid");
};

/**
 * Récupère le Click ID (fbc) pour la CAPI.
 * Ordre : cookie _fbc, sinon construction depuis fbclid (format: fb.1.creationTime.fbclid).
 * Améliore l’Event Match Quality et l’attribution des conversions.
 */
const getFbc = () => {
  const fromCookie = getCookie("_fbc");
  if (fromCookie) return fromCookie;
  const fbclid = getFbclidFromUrl();
  if (!fbclid) return null;
  const creationTime = Date.now();
  const fbc = `fb.1.${creationTime}.${fbclid}`;
  try {
    document.cookie = `_fbc=${fbc}; path=/; max-age=${90 * 24 * 60 * 60}; SameSite=Lax`;
  } catch (_) {}
  return fbc;
};

/**
 * Récupère le Browser ID (fbp) pour la CAPI.
 * Défini automatiquement par le Meta Pixel (_fbp cookie).
 */
const getFbp = () => getCookie("_fbp");

const useFacebookPixel = (pixelId) => {
  const apolloClient = useApolloClient();

  useEffect(() => {
    if (!window.fbq) {
      (function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod
            ? n.callMethod.apply(n, arguments)
            : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = "2.0";
        n.queue = [];
        t = b.createElement(e);
        t.async = true;
        t.src = "https://connect.facebook.net/en_US/fbevents.js";
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(window, document, "script");
    }

    if (pixelId && window.fbq) {
      window.fbq("init", pixelId);
      window.fbq("track", "PageView");
    }
  }, [pixelId]);

  /**
   * Normalise le téléphone en E.164 (chiffres uniquement, sans espaces).
   * Requis par Meta CAPI pour le matching.
   */
  const toE164Phone = (phone) => {
    if (!phone) return null;
    const digits = String(phone).replace(/\D/g, "");
    return digits || null;
  };

  /**
   * Envoie l'événement au CAPI (Conversions API) pour stratégie hybride.
   * Inclut fbc (Click ID) et fbp (Browser ID) pour améliorer l’Event Match Quality.
   */
  const sendToCAPI = (eventName, eventId, parameters, isCustom = false) => {
    if (!apolloClient) return;

    const params = parameters || {};
    const userData = params.userData;

    const buildUserData = (ud) => {
      if (!ud) return null;
      const built = {
        firstName: ud.firstName ?? ud.firstname,
        lastName: ud.lastName ?? ud.lastname,
        phone: toE164Phone(ud.phone),
        email: ud.email,
        city: ud.city,
        country: ud.country,
      };
      return Object.fromEntries(
        Object.entries(built).filter(([, v]) => v != null && v !== "")
      );
    };

    const ud = buildUserData(userData);

    const contentIds =
      params.content_ids ?? params.contentIds;
    const contentType = params.content_type ?? params.contentType ?? null;

    const fbc = getFbc();
    const fbp = getFbp();

    const eventData = {
      eventName: isCustom ? `CustomEvent_${eventName}` : eventName,
      eventId,
      currency: params.currency ?? null,
      eventValue: params.value ?? params.eventValue ?? null,
      eventTime: Math.floor(Date.now() / 1000),
      pixelId: pixelId ?? null,
      ...(fbc && { fbc }),
      ...(fbp && { fbp }),
      ...(Array.isArray(contentIds) && contentIds.length > 0 && { contentIds }),
      ...(contentType && { contentType }),
      ...(ud && Object.keys(ud).length > 0 && { userData: ud }),
    };

    apolloClient
      .mutate({
        mutation: SEND_META_CONVERSION_EVENT,
        variables: { eventData },
      })
      .catch((err) => {
        console.warn("[Meta CAPI] Échec envoi serveur:", err?.message || err);
      });
  };

  /**
   * Déclenche un événement standard Meta (ex: Purchase).
   * Stratégie hybride : Pixel client + CAPI serveur avec event_id identique.
   */
  const trackEvent = (eventName, parameters, eventIdPrefix) => {
    const eventId = generateEventId(eventIdPrefix || eventName?.toLowerCase());

    if (window.fbq) {
      window.fbq("track", eventName, parameters || {}, { eventID: eventId });
    }

    sendToCAPI(eventName, eventId, parameters, false);
  };

  /**
   * Déclenche un événement personnalisé Meta.
   * Stratégie hybride : Pixel client + CAPI serveur avec event_id identique.
   */
  const trackCustomEvent = (eventName, parameters, eventIdPrefix) => {
    const eventId = generateEventId(eventIdPrefix || eventName?.toLowerCase());

    if (window.fbq) {
      window.fbq("trackCustom", eventName, parameters || {}, {
        eventID: eventId,
      });
    }

    sendToCAPI(eventName, eventId, parameters, true);
  };

  return { trackEvent, trackCustomEvent };
};

export default useFacebookPixel;
